import { inject, Injectable } from '@angular/core';
import { Logger } from '@core/logger';
import {
	MatterPackageContentDocumentType,
	MatterPackageContentType,
} from '@models/matter-package';
import { DocumentCategory } from '@models/matter-word-documents';
import { Store } from '@ngxs/store';
import { MatterDocumentPackagesAggregatorState } from '@state/matter/matter-document-packages-aggregator/matter-document-packages-aggregator.state';
import PDFMerger from 'pdf-merger-js/browser';
import { DocumentsDrawerComponent } from 'src/app/private/sale/documents-v2/documents-drawer/documents-drawer.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { CloserSyncInstructionsAPIService } from '../../api-repos/mortgages/closer-sync-instructions.api.service';
import { DocxDocumentService } from '../docx-document/docx-document.service';
import { UploadService } from '../file/upload.service';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '../analytics/analytics.service';
import { MatterPackagesDispatch } from '../../state/matter/matter-packages/matter-packages.dispatch';
import { NavigationService } from '../nav/nav.service';
import { DeletePackageConfirmationModalComponent } from 'src/app/private/sale/documents-v2/matter-packages/delete-package-confirmation-modal/delete-packge-confirmation-modal.component';
import { UI_TYPES } from 'src/app/ui/modal/modal.types';
import { ModalService } from 'src/app/ui/modal/modal.service';

@Injectable({
	providedIn: 'root',
})
export class MatterPackagesService {
	private store = inject(Store);
	private docxDocumentService = inject(DocxDocumentService);
	private uploadService = inject(UploadService);
	private drawerService = inject(DrawerService);
	private closerSyncInstructionsAPIService = inject(
		CloserSyncInstructionsAPIService,
	);
	private modalService = inject(ModalService);
	private analytics = inject(AnalyticsService);
	private matterPackagesDispatch = inject(MatterPackagesDispatch);
	private navService = inject(NavigationService);

	public deleteMatterPackage(
		packageId: string,
		matterId: string,
		analytics: AnalyticsEvent,
	) {
		this.modalService.openModal(DeletePackageConfirmationModalComponent, {
			title: 'Delete Package',
			size: 'sm',
			type: UI_TYPES.DANGER,
			data: {
				subTitle: 'Are you sure you want to delete this package?',
				message:
					'Deleting this package will permanently remove it from your matter. This action cannot be undone.',
			},
			primaryAction: {
				text: 'Delete Package',
				action: () => {
					this.analytics.logEvent(analytics);
					this.modalService.setPrimaryActionLoading(true);

					this.matterPackagesDispatch.DeletePackage(packageId);

					this.modalService.setPrimaryActionLoading(false);
					this.modalService.closeModal();
					this.navService.goToMatter_Documents_Packages(matterId);
				},
			},
			secondaryAction: {
				text: 'Cancel',
				action: () => {
					this.modalService.closeModal();
				},
			},
		});
	}

	async getPackageAsPdfBlob(packageId: string) {
		try {
			const allPackages = await this.store.selectSnapshot(
				MatterDocumentPackagesAggregatorState.getPackagesWithDocuments,
			);
			const pkg = allPackages.find((p) => p.id === packageId);
			if (!pkg) {
				throw new Error('Package not found');
			}

			const merger = new PDFMerger();

			const orderedBlobs = await Promise.all(
				pkg.documents.map((docMeta) =>
					docMeta.document.sourceStorageRef.endsWith('.pdf') &&
					!docMeta.document.categories?.includes(DocumentCategory.SYNC_FORMS) &&
					!docMeta.document.categories?.includes(DocumentCategory.MATTER_FORMS)
						? this.uploadService.getBlob(docMeta.document.sourceStorageRef)
						: this.docxDocumentService.getDocumentAsPdfBlob(docMeta.id),
				),
			);

			for (const blob of orderedBlobs) {
				await merger.add(blob);
			}

			const mergedPdf = await merger.saveAsBlob();

			return mergedPdf;
		} catch (error) {
			console.error('Failed to merge package:', error);
			throw error;
		}
	}
	/**
	 * @deprecated THROW AWAY - FOR OCT8TH DEMO ONLY
	 * @returns {Observable<string>} - the fileBase64 of the content
	 */
	async getSyncPackageAsPdfBlob(packageId: string, matterId: string) {
		try {
			const allPackages = await this.store.selectSnapshot(
				MatterDocumentPackagesAggregatorState.getPackagesWithDocuments,
			);
			const pkg = allPackages.find((p) => p.id === packageId);
			if (!pkg) {
				throw new Error('Package not found');
			}

			const merger = new PDFMerger();

			const blobsPromises = pkg.documents.map(async (document) => {
				if (
					[DocumentCategory.SYNC_FORMS, DocumentCategory.MATTER_FORMS].includes(
						document.document.categories?.[0],
					)
				) {
					return this.closerSyncInstructionsAPIService.prepareIndividualDocumentForPackage(
						{
							matterId: matterId,
							matterPackageContent: {
								contentType: MatterPackageContentType.SYNC_FORM,
								documentType: MatterPackageContentDocumentType.WORD,
								id: document.id,
								ordinal: document.ordinal,
								title: document.document.title,
								url: null,
							},
						},
					);
				} else if (
					document.document.sourceStorageRef.endsWith('.pdf') &&
					!document.document.categories?.includes(
						DocumentCategory.SYNC_FORMS,
					) &&
					!document.document.categories?.includes(DocumentCategory.MATTER_FORMS)
				) {
					return this.uploadService.getBlob(document.document.sourceStorageRef);
				} else {
					return this.docxDocumentService.getDocumentAsPdfBlob(document.id);
				}
			});

			const orderedBlobs = await Promise.all(blobsPromises);

			for (const blob of orderedBlobs) {
				await merger.add(blob);
			}

			const mergedPdf = await merger.saveAsBlob();

			return mergedPdf;
		} catch (error) {
			console.error('Failed to merge package:', error);
			throw error;
		}
	}

	async printPackage(packageId: string) {
		try {
			const blob = await this.getPackageAsPdfBlob(packageId);
			const blobURL = URL.createObjectURL(blob);

			const iframe = document.createElement('iframe');
			iframe.style.cssText =
				'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
			document.body.appendChild(iframe);

			const cleanup = () => {
				URL.revokeObjectURL(blobURL);
				document.body.removeChild(iframe);
			};

			iframe.onload = () => {
				try {
					const iframeWindow = iframe.contentWindow;
					if (!iframeWindow) throw new Error('No iframe window access');

					setTimeout(() => {
						iframeWindow.focus();
						iframeWindow.print();
						window.addEventListener('focus', function onFocus() {
							window.removeEventListener('focus', onFocus);
							setTimeout(cleanup, 1000);
						});
					}, 100);
				} catch (e) {
					console.error('Print failed, falling back to new window:', e);
					const printWindow = window.open(blobURL);
					if (printWindow) {
						printWindow.onload = () => printWindow.print();
					}
					cleanup();
				}
			};

			iframe.src = blobURL;
		} catch (error) {
			console.error('Failed to print package:', error);
			throw error;
		}
	}

	async exportPackage(packageId: string) {
		try {
			const getPackage = await this.store.selectSnapshot(
				MatterDocumentPackagesAggregatorState.getPackageById,
			);
			const pkg = await getPackage(packageId);
			if (!pkg) {
				throw new Error('Package not found');
			}
			const blob = await this.getPackageAsPdfBlob(packageId);

			if (!blob) {
				throw new Error('Failed to generate PDF for export');
			}

			const fileName = pkg.packageName || `Package-${packageId}`;
			const pdfUrl = URL.createObjectURL(blob);

			const a = document.createElement('a');
			a.href = pdfUrl;
			a.download = `${fileName}.pdf`;
			document.body.appendChild(a);
			a.click();

			setTimeout(() => {
				document.body.removeChild(a);
				URL.revokeObjectURL(pdfUrl);
			}, 100);
		} catch (error) {
			Logger.exception(error);
			throw error;
		}
	}
	openDocumentsDrawer(packageId: string): void {
		this.drawerService.openDrawer(DocumentsDrawerComponent, {
			title: 'Add Documents',
			size: 'lg',
			noStyle: true,
			data: { packageId },
		});
	}
}
